import React, { useState, useEffect, useCallback } from "react";
import {
	Box,
	Typography,
	TextField,
	Button,
	Paper,
	CircularProgress, // For loading indicator
	Alert, // For error messages
	Dialog,
	DialogTitle,
	DialogContent,
	IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid"; // More advanced table
import { Search, FileDownload, Close } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

// Define your API endpoint
const API_URL = "https://parallex.approot.ng/audit_logs.php"; // *** IMPORTANT: Change this to your actual PHP API URL ***

export default function AuditLogs() {
	const [auditLogs, setAuditLogs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	// Main search/filter states for the displayed data
	const [globalSearch, setGlobalSearch] = useState("");

	// Filter modal states (temp values)
	const [filterOpen, setFilterOpen] = useState(false);
	const [tempFromDate, setTempFromDate] = useState(null);
	const [tempToDate, setTempToDate] = useState(null);
	const [tempFullName, setTempFullName] = useState("");
	const [tempLogAction, setTempLogAction] = useState("");

	// Applied filter states (trigger data filtering)
	const [appliedFromDate, setAppliedFromDate] = useState(null);
	const [appliedToDate, setAppliedToDate] = useState(null);
	const [appliedFullName, setAppliedFullName] = useState("");
	const [appliedLogAction, setAppliedLogAction] = useState("");

	// Fetch data from API
	const fetchAuditLogs = useCallback(async () => {
		setLoading(true);
		setError(null);
		const token = localStorage.getItem("token");
		try {
			const response = await fetch(API_URL, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
				withCredentials: true, // Important to include cookies
			});
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			const data = await response.json();
			if (!data.status) {
				toast.error(data.message);
			}
			setAuditLogs(data.data);
		} catch (err) {
			console.error("Error fetching audit logs:", err);
			setError(
				`Failed to load audit logs: ${err.message}. Please check API URL.`
			);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchAuditLogs();
	}, [fetchAuditLogs]); // Fetch logs on component mount

	// Filtered data based on applied filters and global search
	const filteredLogs = auditLogs.filter((log) => {
		// Global search (top-left input)
		const matchesGlobalSearch = globalSearch
			? log.fullName.toLowerCase().includes(globalSearch.toLowerCase()) ||
			  log.logAction.toLowerCase().includes(globalSearch.toLowerCase())
			: true;

		// Date range filter
		const createdAt = dayjs(log.createdAt);
		const matchesDateRange =
			(!appliedFromDate ||
				createdAt.isSameOrAfter(dayjs(appliedFromDate), "day")) &&
			(!appliedToDate || createdAt.isSameOrBefore(dayjs(appliedToDate), "day"));

		// Full Name filter (from modal)
		const matchesFullName = appliedFullName
			? log.fullName.toLowerCase().includes(appliedFullName.toLowerCase())
			: true;

		// Log Action filter (from modal)
		const matchesLogAction = appliedLogAction
			? log.logAction.toLowerCase().includes(appliedLogAction.toLowerCase())
			: true;

		return (
			matchesGlobalSearch &&
			matchesDateRange &&
			matchesFullName &&
			matchesLogAction
		);
	});

	// DataGrid column definitions
	const columns = [
		{ field: "id", headerName: "ID", width: 90 },
		{ field: "fullName", headerName: "Full Name", width: 200 },
		{ field: "logAction", headerName: "Action", flex: 1, minWidth: 300 }, // Flex to take remaining space
		{
			field: "createdAt",
			headerName: "Timestamp",
			width: 200,
			valueFormatter: (params) => {
				return params ? dayjs(params).format("YYYY-MM-DD HH:mm:ss") : "N/A";
			},
		},
	];

	// Function to apply filters from modal
	const handleApplyFilter = () => {
		setAppliedFromDate(tempFromDate);
		setAppliedToDate(tempToDate);
		setAppliedFullName(tempFullName);
		setAppliedLogAction(tempLogAction);
		setFilterOpen(false); // Close the modal
	};

	// Function to reset filters in modal and applied filters
	const handleResetFilters = () => {
		setTempFromDate(null);
		setTempToDate(null);
		setTempFullName("");
		setTempLogAction("");
		// Also clear applied filters immediately
		setAppliedFromDate(null);
		setAppliedToDate(null);
		setAppliedFullName("");
		setAppliedLogAction("");
	};

	// Placeholder for export logic
	const handleExport = () => {
		// Implement your export logic here (e.g., CSV, Excel)
		alert("Export functionality not yet implemented.");
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Box className='p-4 bg-white rounded-md shadow-sm'>
				<Typography
					variant='h5'
					sx={{ mb: 3, fontWeight: "bold", color: "#333" }}
				>
					Audit Logs
				</Typography>

				{/* Controls - Search, Filter Button, Export Button */}
				<Box className='flex flex-wrap items-center justify-between gap-3 mb-6'>
					{/* Global Search Input */}
					<TextField
						placeholder='Search name or action'
						variant='outlined'
						size='small'
						value={globalSearch}
						onChange={(e) => setGlobalSearch(e.target.value)}
						sx={{ minWidth: "220px", flexGrow: 1 }}
						InputProps={{
							startAdornment: <Search sx={{ mr: 1, color: "action.active" }} />,
							sx: { borderRadius: "8px", paddingLeft: "8px" },
						}}
					/>

					<Box className='flex items-center gap-3'>
						{/* Filter Button */}
						<Button
							variant='outlined'
							sx={{
								backgroundColor: "white",
								color: "black",
								borderColor: "#ccc",
								"&:hover": { backgroundColor: "#f1f1f1" },
								textTransform: "none",
								borderRadius: "8px",
								minWidth: "80px",
								padding: "8px 16px",
							}}
							onClick={() => {
								// When opening the filter modal, populate temp states with applied states
								setTempFromDate(appliedFromDate);
								setTempToDate(appliedToDate);
								setTempFullName(appliedFullName);
								setTempLogAction(appliedLogAction);
								setFilterOpen(true);
							}}
						>
							Filter
						</Button>
						{/* Export Button */}
						<Button
							variant='contained'
							startIcon={<FileDownload />}
							sx={{
								backgroundColor: "#D54B01",
								"&:hover": { backgroundColor: "#b64000" },
								textTransform: "none",
								borderRadius: "8px",
								minWidth: "80px",
								padding: "8px 16px",
							}}
							onClick={handleExport}
						>
							Export
						</Button>
					</Box>
				</Box>

				{/* Loading, Error, or DataGrid */}
				{loading && (
					<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
						<CircularProgress sx={{ color: "#D54B01" }} />
						<Typography variant='body1' sx={{ ml: 2, mt: 0.5 }}>
							Loading audit logs...
						</Typography>
					</Box>
				)}

				{error && (
					<Alert severity='error' sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				{!loading && !error && (
					<Paper
						sx={{
							height: 600,
							width: "100%",
							boxShadow: "none",
							border: "1px solid #e0e0e0",
							borderRadius: "8px",
						}}
					>
						<DataGrid
							rows={filteredLogs}
							columns={columns}
							pageSizeOptions={[10, 25, 50]}
							initialState={{
								pagination: {
									paginationModel: { pageSize: 10, page: 0 },
								},
							}}
							// You can add sorting, selection, etc. here if needed
							disableRowSelectionOnClick // Prevents selecting rows on click
						/>
					</Paper>
				)}

				{/* Filter Modal */}
				<Dialog
					open={filterOpen}
					onClose={() => setFilterOpen(false)}
					maxWidth='xs'
					fullWidth
					sx={{ "& .MuiDialog-paper": { borderRadius: "12px" } }}
				>
					<DialogTitle
						className='flex justify-between items-center'
						sx={{
							fontWeight: "600",
							fontSize: "1.1rem",
							pb: 1.5,
							pt: 2,
							px: 3,
						}}
					>
						<span>Filter by</span>
						<IconButton onClick={() => setFilterOpen(false)} sx={{ p: 0.5 }}>
							<Close />
						</IconButton>
					</DialogTitle>
					<DialogContent
						dividers
						sx={{ borderBottom: "none", px: 3, pt: 2, pb: 2 }}
						className='space-y-4'
					>
						<Typography variant='body2' color='textSecondary' sx={{ mb: 1 }}>
							Date
						</Typography>
						<Box className='grid grid-cols-2 gap-4'>
							<DatePicker
								label='From'
								value={tempFromDate}
								onChange={(val) => setTempFromDate(val)}
								slotProps={{ textField: { size: "small", fullWidth: true } }}
							/>
							<DatePicker
								label='To'
								value={tempToDate}
								onChange={(val) => setTempToDate(val)}
								slotProps={{ textField: { size: "small", fullWidth: true } }}
							/>
						</Box>
						<TextField
							label='Full Name'
							placeholder='Enter full name'
							variant='outlined'
							fullWidth
							size='small'
							value={tempFullName}
							onChange={(e) => setTempFullName(e.target.value)}
							InputProps={{
								endAdornment: tempFullName && (
									<Button
										onClick={() => setTempFullName("")}
										sx={{ textTransform: "none", minWidth: "unset", p: 0.5 }}
									>
										Clear
									</Button>
								),
							}}
						/>
						<TextField
							label='Log Action'
							placeholder='Enter log action'
							variant='outlined'
							fullWidth
							size='small'
							value={tempLogAction}
							onChange={(e) => setTempLogAction(e.target.value)}
							InputProps={{
								endAdornment: tempLogAction && (
									<Button
										onClick={() => setTempLogAction("")}
										sx={{ textTransform: "none", minWidth: "unset", p: 0.5 }}
									>
										Clear
									</Button>
								),
							}}
						/>
						<Box className='flex justify-between items-center pt-4'>
							<Button
								variant='outlined'
								sx={{
									textTransform: "none",
									borderRadius: "8px",
									borderColor: "#ccc",
									color: "#555",
								}}
								onClick={handleResetFilters}
							>
								Reset
							</Button>
							<Button
								variant='contained'
								sx={{
									backgroundColor: "#D54B01",
									"&:hover": { backgroundColor: "#b64000" },
									textTransform: "none",
									borderRadius: "8px",
								}}
								onClick={handleApplyFilter}
							>
								Apply Filter
							</Button>
						</Box>
					</DialogContent>
				</Dialog>
			</Box>
		</LocalizationProvider>
	);
}
